import React, { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { Popover, Tooltip } from '@mui/material';

// Columns Icon matching design [ ||| ]
const ColumnsIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M9 3v18" />
    <path d="M15 3v18" />
  </svg>
);

// Drag Handle Icon ::
const GripVerticalIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <circle cx="9" cy="5" r="1.2" />
    <circle cx="9" cy="12" r="1.2" />
    <circle cx="9" cy="19" r="1.2" />
    <circle cx="15" cy="5" r="1.2" />
    <circle cx="15" cy="12" r="1.2" />
    <circle cx="15" cy="19" r="1.2" />
  </svg>
);

// Eye Icon (Visible)
const EyeIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// EyeOff Icon (Hidden)
const EyeOffIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// RotateCcw / Reset Icon
const RotateCcwIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

// Arrow Up Down Icon
const ArrowUpDownIcon = ({ className = 'w-4 h-4' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m7 15 5 5 5-5" />
    <path d="m7 9 5-5 5 5" />
  </svg>
);

export const formatName = (str, fallback = "Default") => {
  if (!str || typeof str !== "string") return fallback;
  return str
    .replace(/[_\-]+/g, " ") // replace underscores/dashes with space
    .replace(/[^a-zA-Z0-9 •/&]/g, "") // remove other special chars except space, •, /, &
    .replace(/\s+/g, " ") // collapse multiple spaces
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize each word
};

const formatColumnLabel = (rawLabel, id) => {
  if (id === 'createdAt' || id === 'created_at') return 'Created';
  if (id === 'updatedAt' || id === 'updated_at') return 'Updated';
  if (id === 'active') return 'Active';
  if (id === 'action' || id === 'actions') return 'Action';
  if (id === 'image' || id === 'Image') return 'Image';
  if (id === 'title') return 'Title';
  if (id === 'portfoliocategory' || id === 'portfolio_category' || id === 'lowercase_category' || id === 'parent_category') return 'Category';
  if (id === 'comment_count') return 'Comments';
  if (id === 'portfolio_count') return 'Portfolio Count';
  if (id === 'description') return 'Description';
  if (rawLabel && typeof rawLabel === 'string' && rawLabel.trim()) return rawLabel;
  if (!id) return '';

  return id
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const SortableColumnRow = ({ id, label, isVisible, onToggleVisibility, isOverlay = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging && !isOverlay ? 0.2 : 1,
  };

  const formattedLabel = formatColumnLabel(label, id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center justify-between px-2.5 py-1.5 rounded-lg transition-colors duration-150 select-none cursor-pointer ${isOverlay
        ? 'bg-[var(--secondary-bg-color)] border border-[var(--border-color)] shadow-xl text-[var(--primary-text-color)]'
        : isDragging
          ? 'bg-[var(--focus-tab-color)] opacity-60'
          : 'text-[var(--primary-text-color)] hover:bg-[var(--focus-tab-color)]'
        }`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="p-0.5 shrink-0 transition-colors cursor-grab active:cursor-grabbing text-[var(--table-text-color)] group-hover:text-[var(--primary-text-color)]"
        >
          <GripVerticalIcon className="w-3.5 h-3.5" />
        </button>
        <span
          onClick={() => onToggleVisibility && onToggleVisibility(id, !isVisible)}
          className={`text-xs font-medium truncate cursor-pointer select-none ${!isVisible
            ? 'text-[var(--table-text-color)] opacity-60'
            : 'text-[var(--primary-text-color)]'
            }`}
        >
          {formatName(formattedLabel)}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onToggleVisibility && onToggleVisibility(id, !isVisible)}
        className={`p-1 rounded-md transition-colors shrink-0 ${isVisible
          ? 'text-[var(--table-text-color)] group-hover:text-[var(--primary-text-color)] hover:bg-[var(--focus-tab-color)]'
          : 'text-[var(--table-text-color)] opacity-40 hover:opacity-80 hover:bg-[var(--focus-tab-color)]'
          }`}
      >
        {isVisible ? <EyeIcon className="w-4 h-4" /> : <EyeOffIcon className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const allowSortPanel = [
  "portfolios",
  "portfoliocomments",
  "portfoliocategory",
];

export const ManageFieldsButton = ({
  tableId,
  allowedSortKeys,
  anchorEl,
  onOpen,
  onClose,
  defaultColumnDefs = [],
  columnOrder = [],
  columnVisibility = {},
  onSaveConfig,
  onResetConfig,
  sortBy,
  orderBy,
  onSort,
  sortOptions,
}) => {
  const [activeDragId, setActiveDragId] = useState(null);
  const [warningMsg, setWarningMsg] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const visibleCount = useMemo(() => {
    return columnOrder.filter((id) => columnVisibility[id] !== false).length;
  }, [columnOrder, columnVisibility]);

  const handleReset = () => {
    if (onResetConfig) onResetConfig();
  };

  const columnMap = useMemo(() => {
    const map = new Map();
    defaultColumnDefs.forEach((col) => {
      let labelStr = col.id || col.key;
      if (typeof col.header === 'string') {
        labelStr = col.header;
      } else if (typeof col.title === 'string') {
        labelStr = col.title;
      } else if (col.header?.props?.children) {
        const child = col.header.props.children;
        if (typeof child === 'string') labelStr = child;
      } else if (col.title?.props?.children) {
        const child = col.title.props.children;
        if (typeof child === 'string') labelStr = child;
      }
      map.set(col.id, { label: labelStr, raw: col });
    });
    return map;
  }, [defaultColumnDefs]);

  // Derive sortable columns list with table-wise allowed keys support
  const sortableCols = useMemo(() => {
    if (Array.isArray(sortOptions) && sortOptions.length > 0) {
      return sortOptions;
    }

    const tableAllowed = allowedSortKeys;
    const allowedSet = tableAllowed ? new Set(tableAllowed.map((k) => k.toLowerCase())) : null;

    const availableCols = defaultColumnDefs.filter((col) => {
      const id = (col.id || col.key || '').toString();
      const lower = id.toLowerCase();

      // If column is hidden in visibility settings, do not show in sort panel
      if (columnVisibility && columnVisibility[id] === false) {
        return false;
      }

      if (
        lower === 'checkbox' ||
        lower === 'selection' ||
        lower === 'select' ||
        lower === 'action' ||
        lower === 'actions' ||
        lower === 'drag_handle' ||
        lower === 'draghandle' ||
        lower === 'drag' ||
        lower === 'image' ||
        col.sortable === false
      ) {
        return false;
      }

      if (allowedSet) {
        return allowedSet.has(lower) || allowedSet.has(id);
      }

      return true;
    });

    // If table-specific list is provided, order them as defined in allowed keys
    if (tableAllowed && Array.isArray(tableAllowed)) {
      const colMap = new Map();
      availableCols.forEach((col) => {
        const id = col.id || col.key;
        colMap.set(id.toLowerCase(), col);
      });

      const orderedCols = [];
      tableAllowed.forEach((key) => {
        const col = colMap.get(key.toLowerCase());
        if (col) {
          const id = col.id || col.key;
          let labelStr = id;
          if (typeof col.header === 'string') labelStr = col.header;
          else if (typeof col.title === 'string') labelStr = col.title;
          else if (col.title?.props?.children && typeof col.title.props.children === 'string') {
            labelStr = col.title.props.children;
          }
          orderedCols.push({
            id,
            label: formatColumnLabel(labelStr, id),
          });
        }
      });

      if (orderedCols.length > 0) {
        return orderedCols;
      }
    }

    return availableCols.map((col) => {
      const id = col.id || col.key;
      let labelStr = id;
      if (typeof col.header === 'string') labelStr = col.header;
      else if (typeof col.title === 'string') labelStr = col.title;
      else if (col.title?.props?.children && typeof col.title.props.children === 'string') {
        labelStr = col.title.props.children;
      }
      return {
        id,
        label: formatColumnLabel(labelStr, id),
      };
    });
  }, [sortOptions, defaultColumnDefs, allowedSortKeys, columnVisibility]);

  // Determine current sort direction and active sort field
  const isAsc = useMemo(() => {
    if (!orderBy) return false;
    if (typeof orderBy === 'string') {
      const upper = orderBy.trim().toUpperCase();
      return upper === 'ASC' || upper === 'A-Z' || upper === '1' || upper === 'TRUE';
    }
    if (typeof orderBy === 'object') {
      if (orderBy.orderByValue !== undefined) {
        const upper = String(orderBy.orderByValue).trim().toUpperCase();
        return upper === 'ASC' || upper === 'A-Z' || upper === '1' || upper === 'TRUE';
      }
      if (orderBy.order_by !== undefined) {
        const upper = String(orderBy.order_by).trim().toUpperCase();
        return upper === 'ASC' || upper === 'A-Z' || upper === '1' || upper === 'TRUE';
      }
      if (orderBy.isDecending !== undefined) {
        return orderBy.isDecending === false || orderBy.isDecending === 'false';
      }
      if (orderBy.isDescending !== undefined) {
        return orderBy.isDescending === false || orderBy.isDescending === 'false';
      }
      if (orderBy.isAscending !== undefined) {
        return orderBy.isAscending === true || orderBy.isAscending === 'true';
      }
    }
    return false;
  }, [orderBy]);

  const activeSortKey = sortBy || 'createdAt';

  const currentSortLabel = useMemo(() => {
    const key = activeSortKey || 'createdAt';
    const found = sortableCols.find((c) => c.id.toLowerCase() === key.toLowerCase());
    if (found) return formatName(found.label);
    return formatName(formatColumnLabel('', key));
  }, [activeSortKey, sortableCols]);

  const handleToggleVisibility = (id, visible) => {
    if (visible && visibleCount >= 20) {
      setWarningMsg('Maximum 20 columns allowed');
      setTimeout(() => setWarningMsg(''), 3000);
      return;
    }
    setWarningMsg('');
    onSaveConfig({
      columnOrder,
      columnVisibility: {
        ...columnVisibility,
        [id]: visible,
      },
    });
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);
      const updatedOrder = arrayMove(columnOrder, oldIndex, newIndex);
      onSaveConfig({
        columnOrder: updatedOrder,
        columnVisibility,
      });
    }
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
  };

  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  const handleModalClose = () => {
    setSortAnchorEl(null);
    if (onClose) onClose();
  };

  const isOpen = Boolean(anchorEl);
  const activeDragItem = activeDragId ? columnMap.get(activeDragId) : null;

  return (
    <>
      <Tooltip title="Columns" arrow>
        <button
          type="button"
          onClick={onOpen}
          className="flex items-center justify-center rounded-lg border transition-colors shrink-0 w-8 h-8 border-[var(--border-color)] bg-[var(--secondary-bg-color)] hover:bg-[var(--focus-tab-color)]"
        >
          <ColumnsIcon className="w-4 h-4 text-[var(--table-text-color)]" />
        </button>
      </Tooltip>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleModalClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            borderRadius: '12px',
            width: '240px',
            maxHeight: 'calc(100vh - 120px)',
            backgroundColor: 'var(--secondary-bg-color)',
            color: 'var(--primary-text-color)',
            border: '1px solid var(--border-color)',
            boxShadow: 'var(--shadow-popover)',
            marginTop: '6px',
            padding: '4px 0',
            overflow: 'hidden',
          },
        }}
      >
        <div className="w-full select-none overflow-hidden flex flex-col max-h-[calc(100vh-130px)]">
          {/* ================= SORT BY ROW ================= */}
          {onSort && (!allowSortPanel || allowSortPanel.includes(tableId)) && (
            <>
              <div className="px-3 py-2.5 flex items-center justify-between border-b border-[var(--border-color)] shrink-0">
                <div
                  className="flex items-center gap-1.5 text-xs font-medium text-[var(--primary-text-color)]"
                >
                  <ArrowUpDownIcon
                    className="w-4 h-4 shrink-0 text-[var(--table-text-color)]"
                  />
                  <span>Sort by</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-md transition-colors text-[var(--primary-text-color)] hover:bg-[var(--focus-tab-color)]"
                >
                  <span className="truncate max-w-[100px]">{currentSortLabel}</span>
                  <ArrowUpDownIcon
                    className="w-3 h-3 shrink-0 text-[var(--table-text-color)]"
                  />
                </button>
              </div>

              {/* Nested Floating Sort Menu */}
              <Popover
                open={Boolean(sortAnchorEl)}
                anchorEl={sortAnchorEl}
                onClose={() => setSortAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                PaperProps={{
                  style: {
                    borderRadius: '12px',
                    width: '165px',
                    maxHeight: '260px',
                    backgroundColor: 'var(--secondary-bg-color)',
                    color: 'var(--primary-text-color)',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'var(--shadow-popover)',
                    padding: '6px',
                    marginTop: '4px',
                  },
                }}
              >
                <div className="flex flex-col text-xs select-none max-h-[240px] overflow-y-auto custom-scrollbar">
                  {sortableCols.map((col) => {
                    const isSelected = activeSortKey.toLowerCase() === col.id.toLowerCase();
                    return (
                      <div
                        key={col.id}
                        onClick={() => {
                          if (onSort) {
                            onSort(col.id, isAsc ? 'ASC' : 'DESC', true);
                          }
                          setSortAnchorEl(null);
                        }}
                        style={{
                          backgroundColor: isSelected ? 'var(--focus-tab-color)' : 'transparent',
                          color: isSelected ? 'var(--primary-text-color)' : 'var(--table-text-color)',
                        }}
                        className={`w-full px-2.5 py-1.5 rounded-lg transition-colors flex items-center text-xs cursor-pointer ${!isSelected
                          ? 'hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)]'
                          : ''
                          }`}
                      >
                        <span
                          className="w-4 shrink-0 text-xs font-bold text-[var(--primary-text-color)]"
                        >
                          {isSelected ? '✓' : ''}
                        </span>
                        <span className="truncate font-medium">{formatName(col.label)}</span>
                      </div>
                    );
                  })}

                  <div
                    className="my-1 border-t border-[var(--border-color)]"
                  />

                  {/* Direction Option: A-Z */}
                  <div
                    onClick={() => {
                      if (onSort) {
                        const key = activeSortKey || sortableCols[0]?.id || 'createdAt';
                        onSort(key, 'ASC', true);
                      }
                      setSortAnchorEl(null);
                    }}
                    style={{
                      backgroundColor: isAsc ? 'var(--focus-tab-color)' : 'transparent',
                      color: isAsc ? 'var(--primary-text-color)' : 'var(--table-text-color)',
                    }}
                    className={`w-full px-2.5 py-1.5 rounded-lg transition-colors flex items-center text-xs cursor-pointer ${!isAsc
                      ? 'hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)]'
                      : ''
                      }`}
                  >
                    <span
                      className="w-4 shrink-0 text-xs font-bold text-[var(--primary-text-color)]"
                    >
                      {isAsc ? '✓' : ''}
                    </span>
                    <span className="font-medium">A-Z</span>
                  </div>

                  {/* Direction Option: Z-A */}
                  <div
                    onClick={() => {
                      if (onSort) {
                        const key = activeSortKey || sortableCols[0]?.id || 'createdAt';
                        onSort(key, 'DESC', true);
                      }
                      setSortAnchorEl(null);
                    }}
                    style={{
                      backgroundColor: !isAsc ? 'var(--focus-tab-color)' : 'transparent',
                      color: !isAsc ? 'var(--primary-text-color)' : 'var(--table-text-color)',
                    }}
                    className={`w-full px-2.5 py-1.5 rounded-lg transition-colors flex items-center text-xs cursor-pointer ${isAsc
                      ? 'hover:bg-[var(--focus-tab-color)] hover:text-[var(--primary-text-color)]'
                      : ''
                      }`}
                  >
                    <span
                      className="w-4 shrink-0 text-xs font-bold text-[var(--primary-text-color)]"
                    >
                      {!isAsc ? '✓' : ''}
                    </span>
                    <span className="font-medium">Z-A</span>
                  </div>
                </div>
              </Popover>
            </>
          )}

          {/* ================= COLUMNS HEADER ================= */}
          <div className="px-3 py-1.5 flex items-center justify-between border-b border-[var(--border-color)] mb-1 shrink-0">
            <span className="text-[11px] font-semibold text-[var(--table-text-color)] uppercase tracking-wider">
              Columns
            </span>
            <Tooltip title="Reset columns" arrow>
              <button
                type="button"
                onClick={handleReset}
                className="p-1 rounded-md text-[var(--table-text-color)] hover:text-[var(--primary-text-color)] hover:bg-[var(--focus-tab-color)] transition-colors shrink-0 flex items-center justify-center"
              >
                <RotateCcwIcon className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
          </div>
          {warningMsg && (
            <div className="px-3 py-1 bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 text-[11px] font-medium border-b border-amber-200 dark:border-amber-900/50 shrink-0 flex items-center justify-between">
              <span>{warningMsg}</span>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <SortableContext items={columnOrder} strategy={verticalListSortingStrategy}>
              <div className="max-h-[260px] overflow-y-auto overflow-x-hidden px-1.5 custom-scrollbar">
                {columnOrder.map((id) => {
                  const info = columnMap.get(id);
                  const isVisible = columnVisibility[id] !== false;
                  return (
                    <SortableColumnRow
                      key={id}
                      id={id}
                      label={info?.label || id}
                      isVisible={isVisible}
                      onToggleVisibility={handleToggleVisibility}
                    />
                  );
                })}
              </div>
            </SortableContext>

            <DragOverlay modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
              {activeDragId && activeDragItem ? (
                <SortableColumnRow
                  id={activeDragId}
                  label={activeDragItem.label || activeDragId}
                  isVisible={columnVisibility[activeDragId] !== false}
                  isOverlay
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </Popover>
    </>
  );
};

export default ManageFieldsButton;
